<template>
    <el-main class="p-4 bg-gray-50">
        <div class="cv-preview">
            <div
                class="cv-page"
                v-for="(page, index) in paginatedSections"
                :key="index"
                :id="'section-page-' + index"
                :class="{
                    'long-page': index > 0,
                    'active-section-highlight':
                        page.section.startsWith(activeSection) ||
                        activeSection === 'title',
                }"
            >
                <!-- Cover page -->
                <template v-if="index === 0">
                    <div class="cv-header">
                        <div class="cv-logo">
                            <img
                                v-if="groupName === 'Deutsche Interim AG'"
                                src="/media/images/web_search/logo.svg"
                                alt="Logo"
                                class="cv-logo-img"
                            />
                            <img
                                v-else-if="groupName === 'Swiss Interim GmbH'"
                                src="/media/images/web_search/logo_swiss.png"
                                alt="Logo"
                                class="cv-logo-img-swiss"
                            />

                            <span class="cv-header-company-name">
                                <template
                                    v-if="groupName === 'Deutsche Interim AG'"
                                >
                                    Deutsche<br />Interim AG
                                </template>
                            </span>
                        </div>
                    </div>
                    <div class="cv-cover-page">
                        <div class="cover-header">
                            <div class="title">
                                {{ translations.dossierTitle }}
                            </div>
                            <div class="name">{{ profileName }}</div>
                        </div>

                        <div
                            v-if="cvData['photo']"
                            :class="[
                                'cover-photo-wrapper',
                                activeSection === 'photo' ? 'highlight' : '',
                            ]"
                        >
                            <img
                                :src="cvData['photo']"
                                alt="Kandidatenfoto"
                                class="cv-photo"
                            />
                        </div>
                        <div
                            v-else
                            :class="[
                                'cover-photo-wrapper',
                                activeSection === 'photo' ? 'highlight' : '',
                            ]"
                        >
                            <img alt="Kandidatenfoto" class="cv-photo" />
                        </div>

                        <div class="cover-meta">
                            <p
                                :class="[
                                    'cover-title-wrapper',
                                    activeSection === 'title'
                                        ? 'highlight'
                                        : '',
                                ]"
                            >
                                <strong>{{
                                    translations.projectRequestFor
                                }}</strong
                                ><br /><span class="document-title">{{
                                    cvData.title || "Title..."
                                }}</span>
                            </p>
                            <p>
                                <strong>{{ translations.submittedBy }}</strong
                                ><br />{{ consultantData.consultant_name }},
                                {{ consultantData.consultant_title }}
                                | {{ consultantData.consultant_city }}, {{ dossierDate }}
                            </p>
                            <p>
                                <strong>{{
                                    translations.contactDetails
                                }}</strong
                                ><br />{{ consultantData.consultant_email }} |
                                {{ consultantData.consultant_mobile }}
                            </p>
                        </div>
                        <div class="cover-page-cv-footer">
                            <p class="cover-disclaimer">
                                {{
                                    translations.disclaimer
                                        .replace(/:groupName/g, groupName)
                                        .replace(
                                            /:violation/g,
                                            groupName.toLowerCase() === 'swiss interim gmbh' ? 'Verstoss' : 'Verstoß'
                                        )
                                }}
                            </p>
                            <hr class="cover-footer-divider" />
                            <table class="cover-footer-table">
                                <tbody>
                                <tr>
                                    <td>
                                        <template
                                            v-if="
                                                groupName ===
                                                'Deutsche Interim AG'
                                            "
                                        >
                                            Deutsche Interim AG<br />
                                            Untermainkai 40<br />
                                            60329 Frankfurt am Main
                                        </template>
                                        <template
                                            v-else-if="
                                                groupName ===
                                                'Swiss Interim GmbH'
                                            "
                                        >
                                            Swiss Interim GmbH<br />
                                            Löwenstrasse 65<br />
                                            8001 Zürich
                                        </template>
                                    </td>
                                    <td>
                                        {{ translations.contactPerson }}<br />
                                        {{ consultantData.consultant_name }},
                                        {{ consultantData.consultant_title
                                        }}<br />
                                        {{ consultantData.consultant_mobile }} |
                                        {{ consultantData.consultant_email }}
                                    </td>
                                    <td>
                                        <template
                                            v-if="
                                                groupName ===
                                                'Deutsche Interim AG'
                                            "
                                        >
                                            www.deutscheinterim.com
                                        </template>
                                        <template
                                            v-else-if="
                                                groupName ===
                                                'Swiss Interim GmbH'
                                            "
                                        >
                                            www.swissinterim.com
                                        </template>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                            <div class="cv-footer-bottom-line"></div>
                        </div>
                    </div>
                </template>

                <!-- Other Pages -->
                <template v-else>
                    <div class="cv-header">
                        <div class="cv-logo">
                            <img
                                v-if="groupName === 'Deutsche Interim AG'"
                                src="/media/images/web_search/logo.svg"
                                alt="Logo"
                                class="cv-logo-img"
                            />
                            <img
                                v-else-if="groupName === 'Swiss Interim GmbH'"
                                src="/media/images/web_search/logo_swiss.png"
                                alt="Logo"
                                class="cv-logo-img-swiss"
                            />

                            <span class="cv-header-company-name">
                                <template
                                    v-if="groupName === 'Deutsche Interim AG'"
                                >
                                    Deutsche<br />Interim AG
                                </template>
                            </span>
                        </div>
                    </div>

                    <div class="cv-section">
                        <div class="section-title">
                            <p class="dossier-title">
                                {{ translations.dossierTitle }}
                            </p>
                            <p class="name-title">{{ profileName }}</p>
                        </div>
                        <div class="section-content">
                            <template
                                v-if="
                                    page.section.startsWith('photo') &&
                                    cvData['photo']
                                "
                            >
                                <img
                                    :src="cvData['photo']"
                                    alt="Uploaded photo"
                                    class="cv-photo"
                                />
                            </template>
                            <template v-else>
                                <div v-html="page.content || '---'"></div>
                            </template>
                        </div>
                    </div>

                    <div class="cv-footer">
                        <table class="cv-footer-table">
                            <tbody>
                            <tr>
                                <td>
                                    <template
                                        v-if="
                                            groupName === 'Deutsche Interim AG'
                                        "
                                    >
                                        Deutsche Interim AG<br />
                                        Untermainkai 40<br />
                                        60329 Frankfurt am Main
                                    </template>
                                    <template
                                        v-else-if="
                                            groupName === 'Swiss Interim GmbH'
                                        "
                                    >
                                        Swiss Interim GmbH<br />
                                        Löwenstrasse 65<br />
                                        8001 Zürich
                                    </template>
                                </td>
                                <td>
                                    {{ translations.contactPerson }}<br />
                                    {{ consultantData.consultant_name }},
                                    {{ consultantData.consultant_title }}<br />
                                    {{ consultantData.consultant_mobile }} |
                                    {{ consultantData.consultant_email }}
                                </td>
                                <td>
                                    <template
                                        v-if="
                                            groupName === 'Deutsche Interim AG'
                                        "
                                    >
                                        www.deutscheinterim.com
                                    </template>
                                    <template
                                        v-else-if="
                                            groupName === 'Swiss Interim GmbH'
                                        "
                                    >
                                        www.swissinterim.com
                                    </template>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        <div class="cv-footer-bottom-line"></div>
                    </div>
                </template>
            </div>
        </div>
    </el-main>
</template>

<script>
import { translations } from "../Locales/translations";

export default {
    name: "CvPreview",
    props: {
        cvData: {
            type: Object,
            required: true,
        },
        consultantData: {
            type: Array,
            required: true,
        },
        activeSection: {
            type: String,
            required: true,
        },
        profileName: {
            type: String,
            required: true,
        },
        paginatedSections: {
            type: Array,
            required: true,
        },
        profileLanguage: {
            type: String,
            required: true,
        },
        groupName: {
            type: String,
            required: true,
        },
        dossierDate: {
            type: String,
            required: true,
        },
    },
    computed: {
        translations() {
            return translations[this.profileLanguage] || translations["de"];
        },
    },
};
</script>

<style scoped>
.activeSectionHighlight {
    border: 2px solid #409eff;
    background-color: #ecf5ff;
    border-radius: 4px;
}

.logo {
    width: 115px;
}

.cv-logo-img {
    width: 75px;
}

.cv-logo-img-swiss {
    width: 140px;
}

.logo-wrapper {
    display: flex;
    align-items: center;
}

.header-company-name {
    fill: #303030;
    font-family: Barlow-Medium, Barlow;
    font-size: 26px;
    font-weight: 500;
    margin-top: 34px;
}

.cv-header-company-name {
    fill: #303030;
    font-family: Barlow-Medium, Barlow;
    font-size: 16px;
    font-weight: 500;
    margin-top: 34px;
}

.logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
}

.logo-swiss {
    width: 200px;
    margin-bottom: 10px;
    margin-top: 34px;
}

.logo-matchmaker {
    width: 300px;
    margin-bottom: 13px;
    margin-top: 67px;
}
.cv-preview {
    width: 794px;
    margin: 0 auto;
    background: #f9f9f9;
}

.cv-page {
    position: relative;
    width: 794px;
    height: 806px;
    background: white;
    border: 1px solid #ccc;
    margin-bottom: 16px;
    overflow: hidden;
    page-break-after: always;
    word-break: break-word;
    white-space: normal;
    height: 1122px;
    padding: 40px;
    box-sizing: border-box;
    page-break-after: always;
    background: white;
    padding: 20px;
    border: 1px solid #ddd;
    min-height: 842px;
    position: relative;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    page-break-after: always;
    display: flex;
    flex-direction: column;
    min-height: 800px;
    padding: 20px;
    box-sizing: border-box;
    border: 1px solid #ddd;
    margin-bottom: 20px;
}

.cv-section {
    flex: 1 1 auto;
}

.cv-footer {
    font-size: 12px;
    color: #666;
    border-top: 1px solid #ccc;
    padding-top: 10px;
    margin-top: 20px;
    text-align: center;
    position: relative;
}

.cv-footer-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 5px;
    text-align: left;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
}

.cv-footer-table td {
    vertical-align: top;
    padding: 0 10px;
    border-left: 1px solid #ccc;
}

.cv-footer-table td:first-child {
    border-left: none;
}

.cv-footer-bottom-line {
    border-bottom: 1px solid #ccc;
    width: 100%;
    margin-top: 10px;
}

.cv-header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 80px;
    padding: 8px 16px;
    text-align: center;
}

.cv-title {
    font-size: 20px;
    font-weight: bold;
}

.cv-logo {
    position: absolute;
    top: 70px;
    right: 16px;
    height: 40px;
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
}

.cv-name {
    margin-top: 4px;
    font-size: 16px;
}

.cv-body {
    position: absolute;
    top: 90px;
    bottom: 16px;
    left: 16px;
    right: 16px;
    overflow: hidden;
}

.cv-section {
    margin-bottom: -11px;
}

.cv-section h3 {
    margin: 8px 0;
    font-size: 16px;
    border-bottom: 1px solid #ddd;
    padding-bottom: 4px;
}

.cv-content {
    font-size: 14px;
}

.cv-preview {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.cv-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
}

.cv-title {
    font-size: 20px;
    font-weight: bold;
}

.cv-name {
    margin-top: 10px;
    font-size: 16px;
    font-weight: 600;
}

.cv-section {
    margin-top: 20px;
}

.section-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 5px;
    border-bottom: 1px solid;
}

.section-title .dossier-title {
    font: normal 400 40px/0px Barlow;
}

.section-title .name-title {
    font: normal 400 30px/24px Barlow;
}

.section-content {
    margin-top: 10px;
    white-space: pre-wrap;
}

.cv-photo {
    max-width: 150px;
    margin-top: 10px;
}

.cv-cover-page {
    padding: 40px;
    font-family: "Arial", sans-serif;
    page-break-after: always;
    margin-top: 25%;
}

.cv-cover-page .title {
    font: normal 400 30px/24px Barlow;
}

.cv-cover-page .name {
    font: normal 400 59px/48px Barlow;
    margin-top: 14px;
}

.cv-cover-page .cover-meta p {
    margin-top: 20px;
    font-size: 21px;
}

.cover-page-cv-footer {
    font-size: 12px;
    color: #666;
    border-top: 1px solid #ccc;
    padding-top: 10px;
    margin-top: 20px;
    position: absolute;
    bottom: 20px;
    width: 88%;
    left: 6%;
    box-sizing: border-box;
}

.cover-page-cv-footer p.cover-disclaimer {
    text-align: justify;
    margin-bottom: 8px;
}

.cover-footer-divider {
    border: none;
    border-top: 1px solid #ccc;
    margin: 8px 0;
}

.cover-footer-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
}

.cover-footer-table td:first-child {
    border-left: none;
}

.cover-footer-table td {
    vertical-align: top;
    padding-right: 15px;
    border-left: 1px solid #ccc;
    padding: 0 10px;
}

.cv-photo {
    width: 150px;
    height: 150px;
    margin: 20px 0;
    border-radius: 50%;
    object-fit: cover;
}

.cover-photo-wrapper {
    text-align: left;
}

.highlight {
    background-color: rgba(0, 120, 215, 0.2);
    border: 1px solid rgba(0, 120, 215, 0.8);
    border-radius: 2px;
}

.cv-page.long-page {
    height: auto;
    min-height: 297mm;
}
</style>
